import {
  addDays,
  format,
  isSameDay,
  startOfMonth,
  startOfWeek,
  subDays,
} from 'date-fns'
import { createContext, useContext } from 'react'
import { useSearchParams } from 'react-router-dom'

import { totalDays } from '../utils/constants'

type GroupByMonth = Record<
  string,
  {
    isCurrentMonth: boolean
    days: number
    shortMonthName: string
    fullMonthName: string
  }
>

type GroupByWeek = { start: string; end: string }[]

const useActivitiesCalendarContext = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const currentDate = searchParams.get('date') || format(new Date(), 'MM-yyyy')

  const [currentMonth, currentYear] = currentDate.split('-').map(Number)

  const firstDayOfMonth = startOfMonth(new Date(currentYear, currentMonth - 1))

  const startDate =
    firstDayOfMonth.getDay() <= 1
      ? startOfWeek(subDays(firstDayOfMonth, 7), { weekStartsOn: 0 })
      : startOfWeek(firstDayOfMonth, { weekStartsOn: 0 })

  const dates = Array.from({ length: totalDays }, (_, i) =>
    addDays(startDate, i),
  )

  function groupByMonth(dates: Date[]) {
    return Object.entries(
      dates.reduce((acc, date) => {
        const monthAndYear = format(date, 'MM-yyyy')

        if (acc[monthAndYear]) {
          acc[monthAndYear].days++
          return acc
        }

        acc[monthAndYear] = {
          isCurrentMonth: monthAndYear === currentDate,
          shortMonthName: format(date, 'LLL'),
          fullMonthName: format(date, 'LLLL'),
          days: 1,
        }

        return acc
      }, {} as GroupByMonth),
    ).map(
      ([
        monthAndYear,
        { isCurrentMonth, days, fullMonthName, shortMonthName },
      ]) => ({
        monthAndYear,
        isCurrentMonth,
        fullMonthName,
        shortMonthName,
        days,
      }),
    )
  }

  function groupByWeek(dates: Date[]) {
    return dates.reduce((acc, date, index) => {
      if (index % 7 !== 0) return acc

      acc.push({
        start: format(date, 'dd/MM'),
        end: format(addDays(date, 6), 'dd/MM'),
      })

      return acc
    }, [] as GroupByWeek)
  }

  function formatDatesToDayOfWeek(dates: Date[]) {
    return dates.map((date) => format(date, 'EEEEE'))
  }

  function getTodayIndex(dates: Date[]) {
    const today = getToday()

    const todayIndex = dates.findIndex((date) => isSameDay(date, today))

    return {
      enabled: todayIndex >= 0,
      index: todayIndex,
      startWeekIndex: Math.floor(todayIndex / 7),
    }
  }

  function getToday() {
    return new Date()
  }

  function nextMonth() {
    const date = format(new Date(currentYear, currentMonth, 1), 'MM-yyyy')

    setSearchParams({ date })
  }

  function previousMonth() {
    const date = format(new Date(currentYear, currentMonth - 2, 1), 'MM-yyyy')

    setSearchParams({ date })
  }

  return {
    months: groupByMonth(dates),
    weeks: groupByWeek(dates),
    days: formatDatesToDayOfWeek(dates),
    today: getTodayIndex(dates),
    dateRange: {
      start: dates[0],
      end: dates[dates.length - 1],
    },
    currentMonth,
    currentYear,
    currentDate,
    nextMonth,
    previousMonth,
  }
}

export type ActivitiesCalendarData = ReturnType<
  typeof useActivitiesCalendarContext
>

export const ActivitiesCalendar = createContext<ActivitiesCalendarData>(
  {} as ReturnType<typeof useActivitiesCalendarContext>,
)

export function ActivitiesCalendarProvider({
  children,
}: {
  children: React.ReactNode
}) {
  return (
    <ActivitiesCalendar.Provider value={useActivitiesCalendarContext()}>
      {children}
    </ActivitiesCalendar.Provider>
  )
}

export const useActivitiesCalendar = () => useContext(ActivitiesCalendar)
